import { useState, useRef } from 'react';
import api from '@/lib/api';
import { Plus, Trash2, Download, Upload, FileSpreadsheet } from 'lucide-react';

const RENEWAL_STATUSES = ['active', 'renewing', 'at_risk', 'lost', 'renewed'];
const HEALTH_OPTIONS = ['green', 'amber', 'red'];
const PAYMENT_STRUCTURES = [
  { value: 'monthly_retainer',     label: 'Monthly retainer (advance)' },
  { value: 'quarterly_retainer',   label: 'Quarterly PO-based' },
  { value: 'pdc',                  label: 'Advance + PDCs' },
  { value: 'advance_full',         label: 'Full upfront' },
  { value: 'milestone_linked',     label: 'Milestone-linked' },
  { value: 'variable_consumption', label: 'Variable / consumption' },
  { value: 'fixed_plus_variable',  label: 'Fixed + variable (MG floor)' },
];

const Field = ({ label, required, children, hint }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wide font-medium block mb-1" style={{ color: '#6B7280' }}>
      {label}{required && <span style={{ color: '#D4726A' }}> *</span>}
    </label>
    {children}
    {hint && <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>{hint}</p>}
  </div>
);

const formatCurrency = (v) => {
  if (!v) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  return Number(v).toLocaleString('en-IN');
};

const healthDot = (h) => {
  const colors = { green: '#2D8B5E', amber: '#BA7517', red: '#D4726A' };
  return <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors[h] || '#9CA3AF' }} />;
};

export default function ContractTable({ contracts, onRefresh, divisionId }) {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    client_name: '', entity: '', scope_of_work: '', annual_value: '', contract_value: '',
    start_date: '', end_date: '', renewal_status: 'active',
    payment_structure: 'monthly_retainer',
    has_concierge: false, has_clubs: false, health: 'green', notes: '',
    division_id: divisionId,
  });

  const resetForm = () => {
    setForm({
      client_name: '', entity: '', scope_of_work: '', annual_value: '', contract_value: '',
      start_date: '', end_date: '', renewal_status: 'active',
      payment_structure: 'monthly_retainer',
      has_concierge: false, has_clubs: false, health: 'green', notes: '',
      division_id: divisionId,
    });
    setEditId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editId) {
      await api.put(`/contracts/${editId}`, form);
    } else {
      await api.post('/contracts', form);
    }
    resetForm();
    onRefresh();
  };

  const handleEdit = (c) => {
    setForm({
      client_name: c.client_name, entity: c.entity || '', annual_value: c.annual_value,
      contract_value: c.contract_value || c.annual_value, start_date: c.start_date || '',
      end_date: c.end_date || '', renewal_status: c.renewal_status || 'active',
      has_concierge: c.has_concierge || false, has_clubs: c.has_clubs || false,
      health: c.health || 'green', notes: c.notes || '', division_id: divisionId,
    });
    setEditId(c.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contract?')) return;
    await api.delete(`/contracts/${id}`);
    onRefresh();
  };

  const totalValue = contracts.reduce((sum, c) => sum + (c.annual_value || 0), 0);

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('division_id', divisionId);
    try { const r = await api.post('/contracts/upload-csv', fd); alert(`Imported ${r.data.imported} contracts${r.data.errors?.length ? `. Errors: ${r.data.errors.join(', ')}` : ''}`); onRefresh(); }
    catch { alert('Upload failed'); }
    e.target.value = '';
  };
  const handleDownload = async (template = false) => {
    const url = template ? '/contracts/csv-template' : `/contracts/download-csv?division_id=${divisionId}`;
    const r = await api.get(url, { responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([r.data]));
    a.download = template ? 'contracts_template.csv' : `contracts_${divisionId}.csv`; a.click();
  };

  return (
    <div className="space-y-4" data-testid="contract-table">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <div>
          <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Contracts ({contracts.length})</h4>
          <p className="text-xs" style={{ color: '#6B7280' }}>Total value: {formatCurrency(totalValue)}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => handleDownload(true)} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}><FileSpreadsheet size={12} /> Template</button>
          <button onClick={() => handleDownload(false)} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}><Download size={12} /> Export</button>
          <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}><Upload size={12} /> Import</button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleUpload} />
          <button onClick={() => { resetForm(); setShowForm(!showForm); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white"
            style={{ backgroundColor: '#2A7B88' }} data-testid="add-contract-btn">
            <Plus size={13} /> Add Contract
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-5 space-y-4" style={{ borderColor: '#E5E1DC' }}
          data-testid="contract-form">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#2A7B88' }}>
            {editId ? 'Edit contract' : 'New contract'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Client name" required>
              <input value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))}
                placeholder="e.g. Xiaomi" required className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}
                data-testid="contract-client" />
            </Field>
            <Field label="Contract title / entity" hint="e.g. 'RedBeryl · CC Loyalty · FY26-27'">
              <input value={form.entity} onChange={e => setForm(p => ({ ...p, entity: e.target.value }))}
                placeholder="Short identifier for this contract" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            </Field>
            <Field label="Start date" required>
              <input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                required className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="contract-start-date" />
            </Field>
            <Field label="End date" required>
              <input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                required className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="contract-end-date" />
            </Field>
            <Field label="Annual value (₹)" required hint="Total this client will bill per year">
              <input type="number" value={form.annual_value} onChange={e => setForm(p => ({ ...p, annual_value: e.target.value, contract_value: p.contract_value || e.target.value }))}
                placeholder="e.g. 1200000" required className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}
                data-testid="contract-value" />
            </Field>
            <Field label="Contract value (₹)" hint="Total over full term. Defaults to annual value.">
              <input type="number" value={form.contract_value} onChange={e => setForm(p => ({ ...p, contract_value: e.target.value }))}
                placeholder="Optional" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            </Field>
            <Field label="Payment structure" hint="Auto-generates the payment schedule in the Dossier">
              <select value={form.payment_structure} onChange={e => setForm(p => ({ ...p, payment_structure: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm bg-white" style={{ borderColor: '#E5E1DC' }} data-testid="contract-structure">
                {PAYMENT_STRUCTURES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Renewal status">
              <select value={form.renewal_status} onChange={e => setForm(p => ({ ...p, renewal_status: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm bg-white" style={{ borderColor: '#E5E1DC' }}>
                {RENEWAL_STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </Field>
            <Field label="Health">
              <select value={form.health} onChange={e => setForm(p => ({ ...p, health: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm bg-white" style={{ borderColor: '#E5E1DC' }}>
                {HEALTH_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </Field>
            <Field label="Scope of work" hint="Optional. What we're actually delivering.">
              <input value={form.scope_of_work} onChange={e => setForm(p => ({ ...p, scope_of_work: e.target.value }))}
                placeholder="e.g. Employee concierge · benefits desk · events" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            </Field>
          </div>
          <Field label="Scope flags">
            <div className="flex items-center gap-4 pt-1">
              <label className="flex items-center gap-1.5 text-xs" style={{ color: '#6B7280' }}>
                <input type="checkbox" checked={form.has_concierge} onChange={e => setForm(p => ({ ...p, has_concierge: e.target.checked }))} />
                Concierge services
              </label>
              <label className="flex items-center gap-1.5 text-xs" style={{ color: '#6B7280' }}>
                <input type="checkbox" checked={form.has_clubs} onChange={e => setForm(p => ({ ...p, has_clubs: e.target.checked }))} />
                Clubs access
              </label>
            </div>
          </Field>
          <Field label="Notes">
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Anything important about this contract" rows={2} className="w-full px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC' }} />
          </Field>
          <div className="flex items-center justify-between pt-1 border-t" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[11px]" style={{ color: '#9CA3AF' }}>
              💡 Payment rows will auto-generate in the Client Dossier &gt; Payments tab based on your billing structure. You can edit/add/delete rows there anytime.
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={resetForm} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 rounded-md text-sm font-medium text-white"
                style={{ backgroundColor: '#1B2A4A' }} data-testid="contract-submit">
                {editId ? 'Update contract' : 'Add contract'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: '#F7F5F2' }}>
                <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Client</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium hidden sm:table-cell" style={{ color: '#6B7280' }}>Entity</th>
                <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Annual Value</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>End Date</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Status</th>
                <th className="text-center px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Health</th>
                <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {contracts.map(c => (
                <tr key={c.id} data-testid={`contract-row-${c.id}`}>
                  <td className="px-3 py-2.5 font-medium" style={{ color: '#1B2A4A' }}>{c.client_name}</td>
                  <td className="px-3 py-2.5 hidden sm:table-cell" style={{ color: '#6B7280' }}>{c.entity}</td>
                  <td className="px-3 py-2.5 text-right" style={{ color: '#1B2A4A' }}>{formatCurrency(c.annual_value)}</td>
                  <td className="px-3 py-2.5" style={{ color: '#6B7280' }}>{c.end_date || '-'}</td>
                  <td className="px-3 py-2.5">
                    <span className="text-xs capitalize" style={{ color: '#6B7280' }}>{c.renewal_status?.replace('_', ' ')}</span>
                  </td>
                  <td className="px-3 py-2.5 text-center">{healthDot(c.health)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(c)} className="p-1 rounded hover:bg-gray-100 text-xs" style={{ color: '#2A7B88' }}>Edit</button>
                      <button onClick={() => handleDelete(c.id)} className="p-1 rounded hover:bg-gray-100">
                        <Trash2 size={13} style={{ color: '#D4726A' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {contracts.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-sm" style={{ color: '#9CA3AF' }}>No contracts yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
